import { useRouter } from "next/router";
import { useMemo } from "react";
import { useLoyaltyContext } from "../../../../../../services/hooks/loyalty/useLoyaltyContext";
import { useCustomerAnalytics } from "../../../../../../services/hooks/useCustomerAnalytics";
import { useModalHook } from "../../../../../../services/hooks/useModal";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../../../services/hooks/useReduxHooks";
import { useTranslationI18 } from "../../../../../../services/hooks/useTranslationI18";
import { selectAppliedOfferGraphicEntityIds } from "../../../../../../services/redux/selectors/loyaltyCartSelectors";
import { selectRedeemedLoyaltyOffer } from "../../../../../../services/redux/selectors/loyaltyOfferSelectors";
import { selectIsPayWithPointsApplied } from "../../../../../../services/redux/selectors/payWithPointsSelectors";
import { setBottomSheetState } from "../../../../../../services/redux/slices/globalComponentsToggleSlice";
import { setOfferData } from "../../../../../../services/redux/slices/userInfoSlice";
import {
  CUSTOMER_ANALYTICS_EVENT,
  OfferCardSize,
  PagesEnum,
} from "../../../../../../utils/enums";
import { capitalizeFirstLetter } from "../../../../../../utils/help/textPreparation/textPreparation";
import { isGuestBuiltOfferRedeemed } from "../../../../../../utils/loyalty/offerSelection";
import { isVisitor } from "../../../../../../utils/help/user/user";
import LoyaltyOffer from "../../../../../../utils/LoyaltyOfferClass/LoyaltyOffer";
import {
  LoyaltyOfferGraphic,
  OfferBackgroundTextStyle,
} from "../../../../../../utils/loyaltyTypes";
import { useAuth } from "../../../../../Layout/auth/useAuth";

type OfferCardTextLine = {
  text: string;
  useAlternateStyle: boolean;
};

export const useLoyaltyOfferCard = (props: {
  offer: LoyaltyOffer;
  isActiveOffer: boolean;
  size: OfferCardSize;
  disabled?: boolean;
  onClick?: () => void;
  pointsToUnlock?: number;
}) => {
  const { offer, isActiveOffer, size, disabled, onClick, pointsToUnlock } =
    props;

  const { t } = useTranslationI18();
  const router = useRouter();
  const dispatch = useAppDispatch();
  const { handleModal } = useModalHook();
  const addCustomerAnalytics = useCustomerAnalytics();
  const authApp = useAuth();
  const { lang, timeZone, mappedOrderType, channel, selectedFranchiseId } =
    useLoyaltyContext();

  const restaurantInfo = useAppSelector((state) => state.config.restaurantInfo);
  const bottomSheetModal = useAppSelector(
    (state) => state.globalComponentsToggleSlice.bottomSheetModal
  );
  const entityImageLookup = useAppSelector(
    (state) => state.config.entityImageLookup
  );
  const itemStatusRedux = useAppSelector((state) => state.itemStatus);
  const redeemedOffer = useAppSelector(selectRedeemedLoyaltyOffer);
  const appliedGraphicEntityIds = useAppSelector(
    selectAppliedOfferGraphicEntityIds
  );

  const isLocked = !!(pointsToUnlock && pointsToUnlock > 0);
  const redeemText = t("pointing-system:redeem");
  const removeI18 = t("offers:remove");
  const stateLabel = capitalizeFirstLetter(
    isActiveOffer ? removeI18 : redeemText
  );
  const isMainDark = restaurantInfo?.colors?.theme === "main_dark";

  const isOfflineModePage = router.pathname.includes(PagesEnum.OfflineMode);

  const shouldOpenBottomSheet =
    restaurantInfo &&
    !bottomSheetModal.isOpen &&
    !isOfflineModePage &&
    (!selectedFranchiseId || !mappedOrderType);

  const loyaltyGraphics = offer.uiConfig?.graphics ?? [];
  const imageLookup = useMemo(() => entityImageLookup, [entityImageLookup]);

  /** Image-type graphics from offer config (order preserved). */
  const offerImageGraphics = loyaltyGraphics.filter(
    (item): item is Extract<LoyaltyOfferGraphic, { type: "image" }> =>
      item.type === "image"
  );
  const offerImageGraphicsCount = offerImageGraphics.length;

  /**
   * Central card image vs gift icon (`FreeItemIcon`):
   * - More than one image graphic → always gift (multi-item / bundle signal).
   * - Exactly one image graphic → show that item’s photo when `entityImageLookup` has a URL.
   * - Exactly one image graphic but no lookup URL → gift (missing asset fallback).
   */
  const centralOfferImageUrl =
    offerImageGraphicsCount === 1
      ? imageLookup[String(offerImageGraphics[0].entityId)]?.itemImage
      : undefined;

  /*
   * A BOGO or bundle shows the ring of item images rather than one icon or
   * background text, and narrows to the guest's own picks once it is applied.
   * The ids only describe the offer currently in the cart, so they are passed
   * to that offer's card alone — every other card still shows its full set.
   */
  const collage = isGuestBuiltOfferRedeemed(offer)
    ? {
        graphics: loyaltyGraphics,
        includeEntityIds:
          redeemedOffer?.id === offer.id ? appliedGraphicEntityIds : undefined,
      }
    : undefined;

  const hasImageGraphic = offerImageGraphicsCount > 0;
  const shouldUseGiftIconForOfferGraphics =
    hasImageGraphic &&
    (offerImageGraphicsCount > 1 ||
      (offerImageGraphicsCount === 1 && !centralOfferImageUrl));

  const offerTitle: OfferCardTextLine[] = (
    offer.uiConfig?.offerCard?.titleText?.[lang] || []
  ).map((text) => ({
    text,
    useAlternateStyle: false,
  }));

  const mappedBackgroundText: OfferCardTextLine[] = (
    offer.uiConfig?.offerCard?.backgroundText?.[lang] || []
  ).map((line) => ({
    text: line.text,
    useAlternateStyle: line.style === OfferBackgroundTextStyle.Alternate,
  }));

  const offerBgText = mappedBackgroundText;

  const offerDetails =
    size === OfferCardSize.small || size === OfferCardSize.medium
      ? offer.uiConfig?.offerCard?.conditions?.short?.[lang]
      : offer.uiConfig?.offerCard?.conditions?.full?.[lang];

  const hasBackgroundText = offerBgText.length > 0;
  const hasBottomSection = Boolean(offer.milestonePoints || hasImageGraphic);

  const applicabilityResult = offer.isOfferApplicable({
    timeZone,
    orderType: mappedOrderType ?? undefined,
    channel,
    entityImageLookup,
    itemStatusRedux,
  });

  const hasDeletedMenuItems = offer.hasDeletedItems(entityImageLookup);

  const isOfferUnavailable = LoyaltyOffer.isLoyaltyOfferBlocked(
    Boolean(disabled),
    applicabilityResult.applicable,
    hasDeletedMenuItems
  );

  const statusChipText = LoyaltyOffer.getLoyaltyOfferStatusChipText(
    isOfferUnavailable,
    applicabilityResult,
    t,
    size === OfferCardSize.large ? undefined : offerDetails
  );

  const isPayWithPointsApplied = useAppSelector(selectIsPayWithPointsApplied);

  const handleRedeemOffer = () => {
    if (
      restaurantInfo?.config?.hasOfflineMode &&
      isOfflineModePage &&
      !isLocked
    ) {
      dispatch(
        setOfferData({
          loyaltyOffer: offer.toPlain(),
          isRedeemed: false,
        })
      );
      if (isVisitor()) {
        authApp.signIn(undefined, true);
      }
      addCustomerAnalytics(CUSTOMER_ANALYTICS_EVENT.OFFLINE_REWARD_OPENED);
      handleModal(true, "OfflineBurningPopup/OfflineBurningPopup", {
        componentProps: { loyaltyOffer: offer },
      });

      return;
    }

    if (shouldOpenBottomSheet) {
      dispatch(setBottomSheetState(true));

      return;
    }

    // ----------------------An applied points discount is the competing reward, so the choice comes before the offer's own popup-----------------------
    if (!isOfferUnavailable && isPayWithPointsApplied) {
      handleModal(true, "LoyaltyOffersPopup/LoyaltyOffersPopup", {
        componentProps: { pendingOffer: offer.toPlain() },
      });

      return;
    }

    if (!isOfferUnavailable) {
      handleModal(true, "LoyaltyRedeemPopup/LoyaltyRedeemPopup", {
        componentProps: {
          offer: offer,
          isActiveOffer: isActiveOffer,
          pointsToUnlock: pointsToUnlock,
        },
      });
    }
  };

  const handleCardClick = () => {
    if (onClick) {
      onClick();

      return;
    }

    if (isLocked) {
      addCustomerAnalytics(CUSTOMER_ANALYTICS_EVENT.LOCKED_REWARDS_CLICK);
    }

    handleRedeemOffer();
  };

  const createMarkup = (html: string) => ({ __html: html });

  return {
    isOfferUnavailable,
    statusChipText,
    isLocked,
    isMainDark,
    hasBottomSection,
    hasBackgroundText,
    offerTitle,
    offerBgText,
    offerDetails,
    centralOfferImageUrl,
    collage,
    shouldUseGiftIconForOfferGraphics,
    stateLabel,
    handleCardClick,
    createMarkup,
  };
};
